import React, { Fragment } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Collapse,
  Button,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import { formatDayMonthTime } from "@/utils/formatDateTime";
import type { SitePermission } from "@/services/userServices/useCurrentPermission";

/** One audit-log row of GET /api/histories, as this timeline reads it. */
export type HistoryEntry = {
  _id: string;
  createdAt: string;
  description?: string;
  user?: { name?: string } | null;
};

export type DisplayHistoriesViewProps = {
  alertNode: React.ReactNode;
  histories: HistoryEntry[];
  permission: Readonly<SitePermission>;
  openedCollapses: string[];
  collapsesToggle: (collapseKey: string) => void;
  questionAlert: (e: React.MouseEvent, element: HistoryEntry) => void;
  clearHistoryAlert: () => void;
};

const DisplayHistoriesView = ({
  alertNode,
  histories,
  permission,
  openedCollapses,
  collapsesToggle,
  questionAlert,
  clearHistoryAlert,
}: DisplayHistoriesViewProps) => {
  return (
    <>
      {/* ALERT */}
      {alertNode}

      {/* NOTIFICATIONS */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <Card className="card-plain">
        <CardHeader
          role="tab"
          onClick={() => collapsesToggle("collapseOne")}
          aria-expanded={openedCollapses.includes("collapseOne")}
        >
          <h3 className="mb-0 text-center text-primary">History</h3>
        </CardHeader>

        <Collapse role="tabpanel" isOpen={openedCollapses.includes("collapseOne")}>
          <CardBody>
            {(permission?.edit || permission?.publish) ? (
              <Row className="float-right mr-6">
                <Button
                  className="btn-round btn-icon"
                  color="danger"
                  id="tooltip_clear_history"
                  onClick={clearHistoryAlert}
                  size="sm"
                >
                  <span className="btn-inner--icon mr-1">
                    <i className="fas fa-trash" />
                  </span>
                  <span className="btn-inner--text">Clear History</span>
                </Button>
              </Row>
            ) : null}

            <Row>
              <CardBody>
                <div
                  className="timeline timeline-one-side"
                  data-timeline-axis-style="dashed"
                  data-timeline-content="axis"
                >
                  {histories.map((value) => (
                    <Fragment key={value._id}>
                      <Row>
                        <Col md="11">
                          <div className="timeline-block">
                            <span className="timeline-step badge-success">
                              <i className="fas fa-bell" />
                            </span>
                            <div className="timeline-content">
                              <small className="text-muted font-weight-bold">
                                {formatDayMonthTime(value.createdAt)}
                              </small>
                              <h5 className="mt-3 mb-0">{value.description}</h5>
                              <p className="text-sm mt-1 mb-0">
                                by&nbsp;{value.user?.name}
                              </p>
                            </div>
                          </div>
                        </Col>

                        {(permission?.edit || permission?.publish) ? (
                          <Col md="1">
                            <i
                              className="ni ni-fat-remove text-warning fa-1x"
                              onClick={(e) => questionAlert(e, value)}
                              role="button"
                              aria-label="Delete history entry"
                            />
                          </Col>
                        ) : null}
                      </Row>
                    </Fragment>
                  ))}
                </div>
              </CardBody>
            </Row>
          </CardBody>
        </Collapse>
      </Card>
    </>
  );
};

export default DisplayHistoriesView;
